import { useMemo, useState } from 'react';
import { Box } from '@chakra-ui/react';
import { Checkbox, Text, Colors } from '@mercator-gcp/grout';
import SearchInput from 'components/elements/SearchInput';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import Header from 'components/widgets/SearchFilterWizard/steps/components/Header';
import SectionWrapper from 'components/widgets/SearchFilterWizard/steps/components/SectionWrapper';
import SelectPillButton from 'components/widgets/SearchFilterWizard/steps/components/SelectPillButton';
import { useDashboardVerticals } from 'state/dashboard';
import { useSectors } from 'state/projectTypes';

const titleStyle = {
  fontSize: '24px',
  fontWeight: 600,
  color: Colors.ArmyGreen900,
  marginBottom: '8px',
};

const searchContainerStyle = {
  marginBottom: 'padding_8',
};

const sectorButtonsContainerStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'padding_12',
  marginBottom: 'padding_16',
  '@media (max-width: 1280px)': {
    gap: 'padding_8',
    marginBottom: 'padding_12',
  },
  '@media (max-width: 1024px)': {
    gap: 'padding_8',
    marginBottom: 'padding_8',
  },
};

const checkboxContainerStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: 'padding_8',
  marginTop: 'padding_8',
  maxHeight: '400px',
  overflowY: 'auto',
  '@media (max-width: 1024px)': {
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 'padding_4',
  },
};

const checkboxItemStyle = {
  display: 'flex',
  alignItems: 'center',
  padding: 'padding_4',
};

const TITLE = 'Which industries sectors are you targeting?';

type SectorsStepProps = {
  selectedVerticals: number[];
  onVerticalsChange: (verticals: number[]) => void;
};

const SectorsStep = ({ selectedVerticals, onVerticalsChange }: SectorsStepProps) => {
  const { verticals: allVerticals, sectorsLoading, verticalsLoading } = useSectors();
  const [, setVerticals] = useDashboardVerticals();
  const [searchQuery, setSearchQuery] = useState('');

  const sortedVerticals = useMemo(() => {
    return [...allVerticals].sort((a, b) => a.name.localeCompare(b.name));
  }, [allVerticals]);

  // Group verticals by sector
  const verticalsBySector = useMemo(() => {
    const grouped = new Map<string, typeof allVerticals>();
    allVerticals.forEach((vertical) => {
      const sectorName = vertical.sector?.name || 'Other';
      if (!grouped.has(sectorName)) {
        grouped.set(sectorName, []);
      }
      grouped.get(sectorName)!.push(vertical);
    });

    return grouped;
  }, [allVerticals]);

  // Get unique sector names, sorted
  const sectorNames = useMemo(() => {
    return Array.from(verticalsBySector.keys()).sort();
  }, [verticalsBySector]);

  const filteredVerticals = useMemo(() => {
    if (!searchQuery.trim()) {
      return sortedVerticals;
    }
    const query = searchQuery.toLowerCase();

    return sortedVerticals.filter((vertical) =>
      vertical.name.toLowerCase().includes(query),
    );
  }, [sortedVerticals, searchQuery]);

  // Get filtered verticals by sector (for sector buttons)
  const filteredVerticalsBySector = useMemo(() => {
    const grouped = new Map<string, typeof filteredVerticals>();
    filteredVerticals.forEach((vertical) => {
      const sectorName = vertical.sector?.name || 'Other';
      if (!grouped.has(sectorName)) {
        grouped.set(sectorName, []);
      }
      grouped.get(sectorName)!.push(vertical);
    });

    return grouped;
  }, [filteredVerticals]);

  const handleVerticalToggle = (verticalId: number) => {
    const newVerticals = selectedVerticals.includes(verticalId)
      ? selectedVerticals.filter((id) => id !== verticalId)
      : [...selectedVerticals, verticalId];

    onVerticalsChange(newVerticals);
    setVerticals(newVerticals);
  };

  const handleSelectAllSector = (sectorName: string) => {
    const sectorVerticals = filteredVerticalsBySector.get(sectorName) || [];
    const sectorVerticalIds = sectorVerticals.map((v) => v.id);
    const allSectorSelected = sectorVerticalIds.every((id) =>
      selectedVerticals.includes(id),
    );

    if (allSectorSelected) {
      // Deselect all verticals in this sector
      const newVerticals = selectedVerticals.filter(
        (id) => !sectorVerticalIds.includes(id),
      );
      onVerticalsChange(newVerticals);
      setVerticals(newVerticals);
    } else {
      // Select all verticals in this sector (merge with existing selections)
      const newVerticals = Array.from(
        new Set([...selectedVerticals, ...sectorVerticalIds]),
      );
      onVerticalsChange(newVerticals);
      setVerticals(newVerticals);
    }
  };

  if (sectorsLoading || verticalsLoading) {
    return (
      <SectionWrapper>
        <Text.Lead sx={titleStyle}>{TITLE}</Text.Lead>
        <Text.Small>Loading sectors...</Text.Small>
      </SectionWrapper>
    );
  }

  return (
    <SectionWrapper>
      <Header
        title={TITLE}
        description="Choose the industries you'd like to focus on for your next project."
      />
      {sortedVerticals.length > 0 && (
        <FlexColumn sx={{ gap: 'padding_8' }}>
          {/* Sector selection buttons - always show all categories */}
          <Box sx={sectorButtonsContainerStyle}>
            {sectorNames.map((sectorName) => {
              // Get filtered verticals for this sector (based on search query)
              const sectorVerticals = filteredVerticalsBySector.get(sectorName) || [];
              // Get all verticals for this sector (regardless of search)
              const allSectorVerticals = verticalsBySector.get(sectorName) || [];

              // Check if any sectors in this category are selected (even if not visible due to search)
              // This ensures the button shows as selected if sectors are selected, regardless of search filter
              const allSectorVerticalIds = allSectorVerticals.map((v) => v.id);
              const hasAnySelected = allSectorVerticalIds.some((id) =>
                selectedVerticals.includes(id),
              );
              // Also check if all filtered sectors are selected (for when search is active)
              const sectorVerticalIds = sectorVerticals.map((v) => v.id);
              const allFilteredSelected =
                sectorVerticalIds.length > 0 &&
                sectorVerticalIds.every((id) => selectedVerticals.includes(id));
              // Show as selected if any sectors in the category are selected OR all filtered sectors are selected
              const allSectorSelected = hasAnySelected || allFilteredSelected;

              return (
                <SelectPillButton
                  key={sectorName}
                  isSelected={allSectorSelected}
                  onClick={() => handleSelectAllSector(sectorName)}
                >
                  All {sectorName}
                </SelectPillButton>
              );
            })}
          </Box>
          <Box sx={searchContainerStyle}>
            <SearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search sectors"
              showClear
            />
          </Box>
          {filteredVerticals.length > 0 && (
            <Box sx={checkboxContainerStyle}>
              {filteredVerticals.map((vertical) => {
                const isChecked = selectedVerticals.includes(vertical.id);

                return (
                  <Box key={vertical.id} sx={checkboxItemStyle}>
                    <Checkbox
                      isChecked={isChecked}
                      onToggle={() => handleVerticalToggle(vertical.id)}
                    >
                      <Text.Body>{vertical.name}</Text.Body>
                    </Checkbox>
                  </Box>
                );
              })}
            </Box>
          )}
          {filteredVerticals.length === 0 && searchQuery.trim() && (
            <Text.Small
              sx={{ color: Colors.TextQuiet, textAlign: 'center', padding: 'padding_24' }}
            >
              No industries found matching &quot;{searchQuery}&quot;
            </Text.Small>
          )}
        </FlexColumn>
      )}
    </SectionWrapper>
  );
};

export default SectorsStep;
